import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem, Chip, Grid,
  CircularProgress, Alert, Snackbar, Tooltip, IconButton, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, getDocs, updateDoc, addDoc, collection, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { generateLabReport } from '../services/reportGenerator';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { isTextParam, isBinaryParam, parseRefBound, evaluateParameter, getRefDisplay } from '../utils/parameterEvaluator';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;

const PARAM_STATUS = {
  normal:   { label: 'Normal',    color: '#16A34A', bg: '#DCFCE7', icon: '🟢' },
  high:     { label: 'Alto ↑',   color: '#DC2626', bg: '#FEE2E2', icon: '🔴' },
  low:      { label: 'Bajo ↓',   color: '#2563EB', bg: '#DBEAFE', icon: '🔵' },
  critical: { label: '⚠ Crítico', color: '#7F1D1D', bg: '#FEE2E2', icon: '⚠️' },
  unknown:  { label: 'Sin ref.',  color: '#6B7280', bg: '#F3F4F6', icon: '⚪' },
  text:     { label: 'Descriptivo', color: '#6D28D9', bg: '#EDE9FE', icon: '📝' },
  positive: { label: 'Positivo +', color: '#C62828', bg: '#FCEBEA', icon: '+' },
  negative: { label: 'Negativo −', color: '#16A34A', bg: '#DCFCE7', icon: '−' },
};

const recalcStatus = (value, ref, refType) => evaluateParameter(value, ref, refType);

export default function ReviewResult() {
  const navigate = useNavigate();
  const { resultId } = useParams();
  const { currentUser, userData } = useAuth();
  const { activeBranch } = useBranch();

  const [result, setResult] = useState(null);
  const [order, setOrder] = useState(null);
  const [patient, setPatient] = useState(null);
  const [owner, setOwner] = useState(null);
  const [vet, setVet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parameters, setParameters] = useState([]);
  const [observations, setObservations] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [examTemplate, setExamTemplate] = useState(null);

  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const load = async () => {
      const resDoc = await getDoc(doc(db, 'labResults', resultId));
      if (!resDoc.exists()) { setLoading(false); return; }
      const res = { id: resDoc.id, ...resDoc.data() };
      setResult(res);
      setObservations(res.observations || '');

      const tplDoc = res.examTypeId ? await getDoc(doc(db, 'examTypes', res.examTypeId)) : null;
      setExamTemplate(tplDoc?.exists() ? { id: tplDoc.id, ...tplDoc.data() } : null);

      const [orderDoc, patDoc] = await Promise.all([
        res.orderId ? getDoc(doc(db, 'labOrders', res.orderId)) : Promise.resolve(null),
        res.patientId ? getDoc(doc(db, 'patients', res.patientId)) : Promise.resolve(null),
      ]);

      let species = '';
      if (orderDoc?.exists()) {
        const o = { id: orderDoc.id, ...orderDoc.data() };
        setOrder(o);
        species = (o.patientSpecies || '').toLowerCase();
        if (o.vetId) {
          const vetDoc = await getDoc(doc(db, 'professionals', o.vetId));
          if (vetDoc.exists()) setVet({ id: vetDoc.id, ...vetDoc.data() });
        }
      }
      if (patDoc?.exists()) {
        const p = { id: patDoc.id, ...patDoc.data() };
        setPatient(p);
        species = species || (p.species || '').toLowerCase();
        if (p.ownerId) {
          const ownerDoc = await getDoc(doc(db, 'owners', p.ownerId));
          if (ownerDoc?.exists()) setOwner({ id: ownerDoc.id, ...ownerDoc.data() });
        }
      }

      // Normalize string: uppercase + remove diacritical marks (á→a, ú→u, etc.)
      const norm = (s) => (s || '').toUpperCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      // Map clinical species names to catalog keys
      const SPECIES_ALIAS = {
        canino: 'perro', felino: 'gato', equino: 'equino',
        bovino: 'bovino', aviar: 'aviar',
      };

      // Enrich parameters with catalog reference ranges (for results with null refMin/refMax)
      const hasNullRefs = (res.extractedParameters || []).some(
        (p) => p.refMin == null || p.refMax == null
      );
      let refLookup = {};
      if (hasNullRefs) {
        try {
          const examCategory = res.examCategory || '';
          const snaps = await Promise.all([
            getDocs(query(collection(db, 'examTypes'), where('category', '==', examCategory))),
            getDocs(query(collection(db, 'examTypes'), where('category', '==', 'Bioquímica'))),
          ]);
          const allDocs = [...snaps[0].docs, ...snaps[1].docs];
          allDocs.forEach((d) => {
            (d.data().parameters || []).forEach((p) => {
              if (p.name && p.refRanges) refLookup[norm(p.name)] = p.refRanges;
            });
          });
          console.log('[ReviewResult] refLookup keys:', Object.keys(refLookup));
          console.log('[ReviewResult] species:', species);
        } catch (err) {
          console.error('[ReviewResult] refLookup error:', err);
        }
      }

      const catalogSpecies = SPECIES_ALIAS[species] || species;

      const enriched = (res.extractedParameters || []).map((p, i) => {
        if (isTextParam(p) || p.status === 'text') {
          return { ...p, ref: null, refType: 'none', refDisplay: '—', refMin: null, refMax: null, status: 'text', isText: true, _idx: i };
        }
        const lookupKey = norm(p.paramName || p.name);
        const catalogParam = examTemplate?.parameters?.find((cp) => {
          const n1 = norm(cp.name);
          return n1 === lookupKey || (cp.originName && norm(cp.originName) === lookupKey);
        });
        const refType = p.refType || catalogParam?.refType || 'minmax';
        let ref = p.ref;
        if (!ref && catalogParam?.refRanges) {
          const r = catalogParam.refRanges[catalogSpecies] || catalogParam.refRanges['default'] || {};
          ref = r;
        }
        // Legacy per-parameter stored refMin/refMax for minmax
        if (refType === 'minmax' && p.refMin != null && p.refMax != null) {
          ref = ref || { min: p.refMin, max: p.refMax };
        }
        const refDisplay = getRefDisplay(ref, refType);
        const isBinary = refType === 'binary';
        const status = recalcStatus(p.value, ref, refType);
        return { ...p, ref, refType, refDisplay, isBinary, status, _idx: i };
      });
      setParameters(enriched);
      setLoading(false);
    };
    load();
  }, [resultId]);

  const handleValueChange = useCallback((idx, field, value) => {
    setParameters((prev) => prev.map((p) => {
      if (p._idx !== idx) return p;
      const updated = { ...p, [field]: value };
      if (field === 'value' && !p.isText) {
        updated.status = recalcStatus(value, p.ref, p.refType);
      }
      return updated;
    }));
  }, []);

  const handleApprove = async () => {
    setApproving(true);
    try {
      const reviewerName = userData?.name || currentUser?.email || 'Técnico';

      // Generate PDF report and trigger direct download
      const reportBlob = await generateLabReport({
        order, result: { ...result, extractedParameters: parameters, observations, reviewedByName: reviewerName, status: 'reported' },
        patient, owner, vet, branch: activeBranch,
      });
      const blobUrl = URL.createObjectURL(reportBlob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `informe-${order?.orderNumber || resultId}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);

      const now = serverTimestamp();
      await updateDoc(doc(db, 'labResults', resultId), {
        extractedParameters: parameters,
        observations,
        status: 'reported',
        reviewedBy: currentUser?.uid || '',
        reviewedByName: reviewerName,
        reviewedAt: now,
        reportGeneratedAt: now,
      });

      if (result?.orderId) {
        await updateDoc(doc(db, 'labOrders', result.orderId), { status: 'reported' });
      }

      await addDoc(collection(db, 'labAuditLog'), {
        resultId,
        orderId: result?.orderId,
        action: 'result_approved',
        performedBy: currentUser?.uid || '',
        performedAt: now,
        details: `Resultado aprobado. Informe generado.`,
      });

      setResult((r) => ({ ...r, status: 'reported' }));
      setSnackbar({ open: true, message: 'Resultado aprobado e informe generado.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await updateDoc(doc(db, 'labResults', resultId), { status: 'pending_upload', processingError: '' });
      if (result?.orderId) {
        await updateDoc(doc(db, 'labOrders', result.orderId), { status: 'pending_upload' });
      }
      await addDoc(collection(db, 'labAuditLog'), {
        resultId,
        action: 'result_rejected',
        performedBy: currentUser?.uid || '',
        performedAt: serverTimestamp(),
      });
      setSnackbar({ open: true, message: 'Resultado rechazado. Se puede volver a procesar.', severity: 'warning' });
      setTimeout(() => navigate('/lab/orders'), 1500);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setRejecting(false);
    }
  };

  const grouped = parameters.reduce((acc, p) => {
    const cat = p.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const criticalCount = parameters.filter((p) => p.status === 'critical').length;
  const unmatchedCount = parameters.filter((p) => p.unmatched).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!result) {
    return <Alert severity="error" sx={{ m: 3 }}>Resultado no encontrado.</Alert>;
  }

  const isApproved = result.status === 'reported' || result.status === 'approved';

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lab/orders')}
            sx={{ color: LAB_PRIMARY, fontWeight: 600 }}>Volver</Button>
          <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>
            Revisión de Resultado
          </Typography>
          <Chip label={result.examTypeName || 'Examen'} size="small"
            sx={{ bgcolor: '#EFF6FF', color: LAB_PRIMARY, fontWeight: 700 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isApproved && (
            <Button variant="outlined" startIcon={<DownloadIcon />}
              onClick={async () => {
                const blob = await generateLabReport({ order, result: { ...result, extractedParameters: parameters, observations }, patient, owner, vet, branch: activeBranch });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `informe-${order?.orderNumber || resultId}.pdf`; a.click();
                URL.revokeObjectURL(url);
              }}
              sx={{ borderColor: LAB_TEAL, color: LAB_TEAL, fontWeight: 700 }}>
              Descargar Informe
            </Button>
          )}
          {!isApproved && (
            <>
              <Button variant="outlined" color="error" startIcon={<CancelIcon />}
                onClick={handleReject} disabled={rejecting || approving}>
                {rejecting ? <CircularProgress size={16} /> : 'Rechazar'}
              </Button>
              <Button variant="contained" startIcon={approving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleApprove} disabled={approving || rejecting}
                sx={{ bgcolor: LAB_TEAL, fontWeight: 700, '&:hover': { bgcolor: '#229984' } }}>
                {approving ? 'Generando informe…' : 'Aprobar y generar informe'}
              </Button>
            </>
          )}
        </Box>
      </Box>
      {/* Summary alerts */}
      {criticalCount > 0 && (
        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 1.5, borderRadius: 2 }}>
          <strong>{criticalCount} parámetro(s) con valor CRÍTICO</strong> — Revisar antes de aprobar.
        </Alert>
      )}
      {unmatchedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
          {unmatchedCount} parámetro(s) no reconocidos en el catálogo — Se muestran como ingresados por OCR.
        </Alert>
      )}
      <Grid container spacing={2}>
        {/* LEFT: original file viewer */}
        <Grid
          size={{
            xs: 12,
            md: 5
          }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, bgcolor: LAB_PRIMARY, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PictureAsPdfIcon sx={{ color: '#fff', fontSize: 18 }} />
              <Typography variant="subtitle2" fontWeight={700} color="#fff">Archivo original</Typography>
              <Chip label={result.originalFileType?.toUpperCase() || 'PDF'} size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.65rem', height: 18 }} />
            </Box>
            {result.originalFileUrl ? (
              result.originalFileType === 'pdf' ? (
                <iframe
                  src={result.originalFileUrl}
                  style={{ width: '100%', height: '72vh', border: 'none' }}
                  title="original"
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, height: '72vh', overflow: 'auto', bgcolor: '#F4F6F8' }}>
                  <img src={result.originalFileUrl} alt="original" style={{ maxWidth: '100%', borderRadius: 8 }} />
                </Box>
              )
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '72vh', color: '#CBD5E1' }}>
                <Typography color="text.disabled">Sin archivo disponible</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* RIGHT: parameter review */}
        <Grid
          size={{
            xs: 12,
            md: 7
          }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0', maxHeight: '80vh', overflowY: 'auto' }}>
            {/* Patient info bar */}
            <Box sx={{ p: 1.5, mb: 2, bgcolor: '#F0F9FF', borderRadius: 2, border: '1px solid #BAE6FD' }}>
              <Typography variant="body2" fontWeight={700} color={LAB_PRIMARY}>
                {result.patientName} — {result.patientSpecies}
              </Typography>
              {order && (
                <Typography variant="caption" color="text.secondary">
                  Orden: {order.orderNumber} · MV: {order.vetName}
                  {order.priority === 'urgent' && (
                    <Chip label="URGENTE" size="small"
                      sx={{ ml: 1, bgcolor: '#FEE2E2', color: '#DC2626', fontSize: '0.6rem', fontWeight: 700, height: 16 }} />
                  )}
                </Typography>
              )}
            </Box>

            {/* Parameters grouped by category */}
            {Object.entries(grouped).map(([category, params]) => (
              <Box key={category} sx={{ mb: 2 }}>
                <Box sx={{ bgcolor: LAB_TEAL, px: 2, py: 0.5, borderRadius: '8px 8px 0 0' }}>
                  <Typography variant="caption" fontWeight={700} color="#fff"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {category}
                  </Typography>
                </Box>
                <TableContainer sx={{ border: '1px solid #E2E8F0', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY }}>Parámetro</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 90 }}>Valor</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 70 }}>Unidad</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 120 }}>Referencia</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 90 }}>Estado</TableCell>
                        {!isApproved && <TableCell sx={{ width: 40 }} />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {params.map((p) => {
                        const st = PARAM_STATUS[p.status] || PARAM_STATUS.unknown;
                        const isEditing = editingRow === p._idx;
                        const isCritical = p.status === 'critical';
                        const isAbnormal = ['high', 'low', 'critical', 'positive'].includes(p.status);
                        return (
                          <TableRow key={p._idx}
                            sx={{ bgcolor: isCritical ? '#FFF5F5' : isAbnormal ? '#FFF5F5' : 'inherit', '&:hover': { bgcolor: '#F8FAFC' } }}>
                            <TableCell sx={{ fontSize: '0.78rem', fontWeight: p.unmatched ? 400 : 600,
                              fontStyle: p.unmatched ? 'italic' : 'normal',
                              color: isCritical ? '#991B1B' : 'inherit' }}>
                              {p.paramName || p.name}
                              {p.unmatched && (
                                <Chip label="No catalogado" size="small"
                                  sx={{ ml: 0.5, fontSize: '0.58rem', height: 15, bgcolor: '#FEF3C7', color: '#92400E' }} />
                              )}
                            </TableCell>
                            {p.isText ? (
                              <TableCell colSpan={3}>
                                {isEditing ? (
                                  <TextField fullWidth size="small" multiline minRows={2} value={p.value}
                                    onChange={(e) => handleValueChange(p._idx, 'value', e.target.value)}
                                    placeholder="Describe la morfología observada…"
                                    inputProps={{ style: { fontSize: '0.78rem' } }} autoFocus />
                                ) : (
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                                    {p.value || '—'}
                                  </Typography>
                                )}
                              </TableCell>
                            ) : p.isBinary ? (
                              <>
                                <TableCell>
                                  {isEditing ? (
                                    <TextField select size="small" value={p.value}
                                      onChange={(e) => handleValueChange(p._idx, 'value', e.target.value)}
                                      inputProps={{ style: { fontSize: '0.78rem', padding: '3px 6px' } }}
                                      sx={{ width: 100 }} autoFocus>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      <MenuItem value="Negativo">Negativo</MenuItem>
                                      <MenuItem value="Positivo">Positivo</MenuItem>
                                    </TextField>
                                  ) : (
                                    <Typography variant="body2" fontWeight={700}
                                      color={p.status === 'positive' ? st.color : 'inherit'}
                                      sx={{ fontSize: '0.82rem' }}>
                                      {p.value || '—'}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.unit || '—'}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.refDisplay}</TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell>
                                  {isEditing ? (
                                    <TextField
                                      size="small" value={p.value}
                                      onChange={(e) => handleValueChange(p._idx, 'value', e.target.value)}
                                      inputProps={{ style: { fontSize: '0.78rem', padding: '3px 6px' } }}
                                      sx={{ width: 80 }}
                                      autoFocus
                                    />
                                  ) : (
                                    <Typography variant="body2" fontWeight={700}
                                      color={['high', 'low', 'critical', 'positive'].includes(p.status) ? st.color : 'inherit'}
                                      sx={{ fontSize: '0.82rem' }}>
                                      {p.value || '—'}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                  {isEditing ? (
                                    <TextField size="small" value={p.unit}
                                      onChange={(e) => handleValueChange(p._idx, 'unit', e.target.value)}
                                      inputProps={{ style: { fontSize: '0.75rem', padding: '3px 4px' } }}
                                      sx={{ width: 65 }}
                                    />
                                  ) : p.unit || '—'}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.refDisplay}</TableCell>
                              </>
                            )}
                            <TableCell>
                              <Chip label={`${st.icon} ${st.label}`} size="small"
                                sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                            </TableCell>
                            {!isApproved && (
                              <TableCell>
                                <Tooltip title={isEditing ? 'Guardar' : 'Editar valor'}>
                                  <IconButton size="small"
                                    onClick={() => setEditingRow(isEditing ? null : p._idx)}
                                    sx={{ color: isEditing ? LAB_TEAL : LAB_PRIMARY }}>
                                    {isEditing ? <SaveIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}

            {parameters.length === 0 && (
              <Alert severity="warning">No se extrajeron parámetros. Intenta rechazar y volver a procesar el archivo.</Alert>
            )}

            {/* Observations */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} color={LAB_PRIMARY} mb={1}>
              Observaciones / Interpretación del técnico
            </Typography>
            <TextField
              fullWidth multiline rows={3} size="small"
              placeholder="Ingresa observaciones clínicas, interpretación o comentarios adicionales…"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              disabled={isApproved}
            />

            {isApproved && (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2, borderRadius: 2 }}>
                Resultado aprobado el {result.reviewedAt?.toDate
                  ? result.reviewedAt.toDate().toLocaleString('es-CL')
                  : '—'}
                {result.reviewedByName ? ` por ${result.reviewedByName}` : ''}.
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
